import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, MessageCircle, MapPin, ChevronDown, ChevronUp, Car, Anchor, Shield, Bike, AlertTriangle, Heart, Scale, Clock, CheckCircle, BookOpen, ArrowRight, FileText, Calculator } from 'lucide-react';
import { getCityBySlug, getAllCities, services } from '@/data/cityData';
import SEOHead from '@/components/SEOHead';

// Resources for city residents - relevant blog articles and tools
const cityResources = [
  {
    title: "What to Do After a Car Accident",
    description: "Step-by-step guide to protect your rights after a crash",
    link: "/blog/what-to-do-after-car-accident-washington",
    icon: FileText
  },
  {
    title: "How Long Do I Have to File?",
    description: "Understanding Washington's statute of limitations",
    link: "/blog/why-statutes-of-limitations-exist-washington-personal-injury",
    icon: Clock
  },
  {
    title: "What Is My Case Worth?",
    description: "Use our free calculator to estimate your compensation",
    link: "/compensation-estimator",
    icon: Calculator
  }
];

const iconMap = {
  Car: Car,
  Anchor: Anchor,
  Shield: Shield,
  Bike: Bike,
  AlertTriangle: AlertTriangle,
  Heart: Heart
};

const CityLandingPage = () => {
  const { slug } = useParams();
  const city = getCityBySlug(slug);
  const [openFaq, setOpenFaq] = useState(0);
  const allCities = getAllCities();

  if (!city) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-bold text-secondary mb-4">City Not Found</h2>
          <Link to="/areas-we-serve" className="text-teal-600 hover:underline">
            View all service areas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid={`city-page-${city.slug}`}>
      <SEOHead
        title={`Best Personal Injury Lawyer in ${city.name}, WA | Amy C. Brown Law`}
        description={`Looking for the best personal injury attorney in ${city.name}? Amy C. Brown has 10+ years experience helping ${city.name} residents with car accidents, maritime injuries & more. Free consultation.`}
        pageType="website"
        localArea={{ city: city.name, county: city.county, state: 'WA' }}
        faqData={city.faqs}
        serviceData={{
          name: `Personal Injury Legal Services in ${city.name}, WA`,
          description: `Personal injury attorney serving ${city.name}, ${city.county} County, Washington. Specializing in car accidents, maritime injuries, motorcycle crashes, first responder injuries, slip and falls, and wrongful death claims.`,
          category: 'Personal Injury Legal Services'
        }}
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-secondary via-secondary to-slate-800">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1920&auto=format')] bg-cover bg-center" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 max-w-6xl relative z-10">
          <div className="flex items-center gap-2 text-primary mb-4">
            <MapPin className="w-5 h-5" />
            <span className="text-sm font-medium">{city.county}, Washington</span>
          </div>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Personal Injury Lawyer in <span className="text-primary">{city.name}</span>, WA
          </h1>
          
          <p className="text-xl text-slate-300 mb-8 max-w-3xl">
            {city.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-bold hover:bg-primary-hover transition-all shadow-lg"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur text-white border border-white/30 px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <Scale className="w-8 h-8 text-primary mb-2" />
              <span className="text-sm font-medium text-secondary">10+ Years Experience</span>
            </div>
            <div className="flex flex-col items-center">
              <CheckCircle className="w-8 h-8 text-primary mb-2" />
              <span className="text-sm font-medium text-secondary">No Fee Unless You Win</span>
            </div>
            <div className="flex flex-col items-center">
              <Phone className="w-8 h-8 text-primary mb-2" />
              <span className="text-sm font-medium text-secondary">Free Consultation</span>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 text-primary mb-2" />
              <span className="text-sm font-medium text-secondary">Fast Response</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-accent-soft">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary text-center mb-4">
            Legal Services in {city.name}
          </h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            I represent {city.name} residents in a wide range of personal injury cases. Here's how I can help:
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Link 
                  key={index}
                  to={service.link}
                  className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 group"
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    {IconComponent && <IconComponent className="w-7 h-7 text-primary" />}
                  </div>
                  <h3 className="font-heading text-xl font-bold text-secondary mb-2">{service.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Helping {city.name} residents pursue compensation for {service.name.toLowerCase()}.
                  </p>
                  <span className="inline-flex items-center gap-1 text-primary font-semibold text-sm group-hover:gap-2 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <h2 className="font-heading text-2xl font-bold text-secondary mb-6">
            Areas Served Near {city.name}
          </h2>
          <div className="flex flex-wrap gap-2">
            {city.landmarks.map((landmark, index) => (
              <span key={index} className="px-4 py-2 bg-slate-100 rounded-full text-sm text-slate-700">
                {landmark}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary text-center mb-4">
            Frequently Asked Questions About Personal Injury Claims in {city.name}
          </h2>
          <p className="text-slate-600 text-center mb-12">
            Get answers to common questions about working with a personal injury lawyer in {city.name}.
          </p>
          
          <div className="space-y-4">
            {city.faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                >
                  <h3 className="font-semibold text-secondary pr-4">{faq.question}</h3>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resources for City Residents - Internal Linking Section */}
      <section className="py-16 bg-white" data-testid="city-resources-section">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <BookOpen className="w-4 h-4" />
              Helpful Resources
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-3">
              Resources for {city.name} Residents
            </h2>
            <p className="text-slate-600">
              Planning your case? These guides will help you understand your rights and options.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {cityResources.map((resource, index) => {
              const IconComponent = resource.icon;
              return (
                <Link
                  key={index}
                  to={resource.link}
                  className="group bg-slate-50 hover:bg-teal-50 rounded-xl p-6 border border-slate-100 hover:border-teal-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-teal-100 group-hover:bg-teal-200 rounded-xl flex items-center justify-center mb-4 transition-colors">
                    <IconComponent className="w-6 h-6 text-teal-700" />
                  </div>
                  <h3 className="font-heading font-bold text-secondary mb-2 group-hover:text-teal-700 transition-colors">
                    {resource.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-3">
                    {resource.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-teal-600 font-medium text-sm group-hover:gap-2 transition-all">
                    Read More <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Injured in {city.name}? Let's Talk.
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Free consultation. No fee unless you win. I personally handle every case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-primary px-8 py-4 rounded-full font-bold hover:bg-slate-100 transition-all shadow-lg"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <Link 
              to="/compensation-estimator"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur text-white border border-white/30 px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all"
            >
              Try the Case Calculator
            </Link>
          </div>
        </div>
      </section>

      {/* Other Service Areas */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <h2 className="font-heading text-2xl font-bold text-secondary mb-8 text-center">
            Other Areas I Serve in Washington
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {allCities
              .filter(c => c.slug !== city.slug)
              .slice(0, 12)
              .map((otherCity, index) => (
                <Link
                  key={index}
                  to={`/service-area/${otherCity.slug}`}
                  className="px-4 py-2 bg-slate-100 hover:bg-primary hover:text-white rounded-full text-sm text-slate-700 transition-colors"
                >
                  {otherCity.name}
                </Link>
              ))}
            <Link
              to="/areas-we-serve"
              className="px-4 py-2 bg-primary text-white rounded-full text-sm font-medium hover:bg-primary-hover transition-colors"
            >
              View All Areas →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityLandingPage;
